import { useState, useEffect } from 'react'
import ApiService from '../../common/ApiCalls'

const useFieldsConfiguration = (setIsLoading, setIsSnackbarOpen) => {
  const [fields, setFields] = useState([])

  const fetchFields = async () => {
    setIsLoading(true)
    try {
      const response = await ApiService().listFields()
      setFields(response)
    } catch (error) {
      console.error('Error fetching fields:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCreateField = async (field) => {
    setIsLoading(true)
    try {
      const createdField = await ApiService().createField({
        label: field.label,
        description: field.description,
        field_type: field.field_type,
        config: field.config,
        archived_at: null,
      })
      if (!createdField?.id) {
        throw new Error('Field creation did not return a field.')
      }

      setFields((previous) => [...previous, createdField])
      setIsSnackbarOpen({
        isOpen: true,
        severity: 'success',
        message: 'Field created successfully',
      })
      return createdField
    } catch (error) {
      console.error('Error creating field:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditField = async (field) => {
    if (!field?.id) return
    setIsLoading(true)
    try {
      const updated = await ApiService().updateField(field.id, {
        label: field.label,
        description: field.description,
        field_type: field.field_type,
        config: field.config,
        archived_at: field.archived_at,
      })
      const updatedField = updated
      if (!updatedField?.id) {
        throw new Error('Field update did not return a field.')
      }

      setFields((prev) =>
        prev.map((f) => (f.id === updatedField.id ? updatedField : f))
      )
      setIsSnackbarOpen({
        isOpen: true,
        severity: 'success',
        message: 'Field updated successfully',
      })
      return updatedField
    } catch (error) {
      console.error('Error updating field:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuspendField = async (field) => {
    if (!field?.id) return
    if (!field.archived_at && !window.confirm(`Suspend "${field.label}"? This will remove it from all layouts. Saved values will be retained. Unsuspending will not restore its layout placements.`)) return
    setIsLoading(true)
    try {
      const archived_at = field.archived_at ? null : new Date().toISOString()
      const updated = await ApiService().updateField(field.id, {
        archived_at,
        label: field.label,
        description: field.description,
        field_type: field.field_type,
        config: field.config,
      })
      const updatedField = updated

      setFields((prev) =>
        prev.map((f) => (f.id === updatedField.id ? updatedField : f))
      )
      setIsSnackbarOpen({ isOpen: true, severity: 'success', message: archived_at ? 'Field suspended and removed from all layouts.' : 'Field unsuspended. Add it to layouts as needed.' })
    } catch (error) {
      console.error('Error suspending field:', error)
      setIsSnackbarOpen({ isOpen: true, severity: 'error', message: 'Unable to change field suspension. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteField = async (fieldId) => {
    setIsLoading(true)
    try {
      await ApiService().deleteField(fieldId)
      setFields((prev) => prev.filter((f) => f.id !== fieldId))
      setIsSnackbarOpen({ isOpen: true, severity: 'success', message: 'Field deleted and removed from all layouts.' })
    } catch (error) {
      console.error('Error deleting field:', error)
      setIsSnackbarOpen({ isOpen: true, severity: 'error', message: 'Unable to delete the field. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    fields,
    handleCreateField,
    handleEditField,
    handleSuspendField,
    handleDeleteField,
  }
}

export default useFieldsConfiguration
